import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Spin } from 'antd';
import api from '../services/api';

const GOLD = '#B8892A';
const TEAL = '#1A6B54';
const INK = '#18140C';
const MUTED = '#685840';
const BG = '#FAF8F5';
const BD = '#E8E0D0';

const BUYER_TIERS = [
  { key: 'v1', labelKey: 'membership.tierV1Full', threshold: 0, color: '#A0A0A0' },
  { key: 'v2', labelKey: 'membership.tierV2Full', threshold: 5000, color: '#8B9DC3' },
  { key: 'v3', labelKey: 'membership.tierV3Full', threshold: 20000, color: GOLD },
];

const SELLER_TIERS = [
  { key: 'gold', labelKey: 'membership.tierGoldSeller', threshold: 0, color: '#D4A843', commission: '12%' },
  { key: 'platinum', labelKey: 'membership.tierPlatinumSeller', threshold: 10000, color: '#8B9DC3', commission: '8%' },
  { key: 'diamond', labelKey: 'membership.tierDiamondSeller', threshold: 50000, color: '#9B59B6', commission: '5%' },
];

export default function MembershipRules() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('buyer');

  useEffect(() => {
    api.get('/customers/my-center').then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ textAlign: 'center', padding: 80 }}><Spin size="large" /></div>;

  const buyerGrowth = Number(data?.buyer_growth || 0);
  const sellerGrowth = Number(data?.seller_growth || 0);
  const buyerTier = data?.buyer_tier || 'v1';
  const sellerTier = data?.seller_tier || 'gold';

  const accentColor = tab === 'buyer' ? TEAL : GOLD;

  return (
    <div style={{ background: BG, minHeight: '100vh', paddingBottom: 40 }}>
      <div style={{ maxWidth: 700, margin: '0 auto' }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', padding: '14px 16px',
          background: '#fff', borderBottom: `1px solid ${BD}`,
        }}>
          <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: INK }}>←</button>
          <span style={{ flex: 1, textAlign: 'center', fontWeight: 600, fontSize: 16, color: INK }}>{t('membership.rules')}</span>
          <div style={{ width: 30 }} />
        </div>

        {/* Tab */}
        <div style={{
          display: 'flex', margin: '16px 16px 0', gap: 0,
          background: '#fff', borderRadius: 8, overflow: 'hidden',
          border: `1px solid ${BD}`,
        }}>
          {[
            { key: 'buyer', label: t('membership.buyer') },
            { key: 'seller', label: t('membership.seller') },
          ].map(item => (
            <button key={item.key} onClick={() => setTab(item.key)} style={{
              flex: 1, padding: '12px 0', border: 'none', cursor: 'pointer',
              fontSize: 14, fontWeight: tab === item.key ? 600 : 400,
              color: tab === item.key ? accentColor : MUTED,
              background: tab === item.key ? `${accentColor}08` : 'transparent',
              borderBottom: tab === item.key ? `2px solid ${accentColor}` : '2px solid transparent',
            }}>{item.label}</button>
          ))}
        </div>

        {/* Growth summary */}
        <div style={{
          margin: '16px 16px 0', padding: '20px',
          background: '#fff', borderRadius: 10, border: `1px solid ${BD}`,
          textAlign: 'center',
        }}>
          <div style={{ fontSize: 12, color: MUTED, marginBottom: 4 }}>
            {tab === 'buyer' ? t('membership.buyer') : t('membership.seller')}
          </div>
          <div style={{ fontSize: 32, fontWeight: 700, color: accentColor }}>
            {(tab === 'buyer' ? buyerGrowth : sellerGrowth).toLocaleString()}
          </div>
          <div style={{ fontSize: 12, color: MUTED, marginTop: 4 }}>
            {t('membership.growthPeriod')}
          </div>
        </div>

        {/* Tier progression */}
        <div style={{ margin: '16px 16px 0' }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: INK, marginBottom: 12 }}>{t('membership.upgrade')}</div>
          {(tab === 'buyer' ? BUYER_TIERS : SELLER_TIERS).map((tier, i, arr) => {
            const current = tab === 'buyer' ? buyerTier : sellerTier;
            const currentGrowth = tab === 'buyer' ? buyerGrowth : sellerGrowth;
            const isCurrent = tier.key === current;
            const isReached = currentGrowth >= tier.threshold;
            const next = arr[i + 1];

            return (
              <div key={tier.key} style={{
                background: '#fff', borderRadius: 10, marginBottom: 10,
                border: isCurrent ? `2px solid ${accentColor}` : `1px solid ${BD}`,
                padding: '16px', position: 'relative',
              }}>
                {isCurrent && (
                  <div style={{
                    position: 'absolute', top: -1, right: 16,
                    padding: '2px 10px', borderRadius: '0 0 6px 6px',
                    background: accentColor, color: '#fff', fontSize: 10,
                  }}>{t('membership.currentLevel')}</div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                  <span style={{ fontSize: 14, fontWeight: 600, color: isReached ? tier.color : '#CCC' }}>
                    {t(tier.labelKey)}
                  </span>
                  {tier.threshold > 0 && (
                    <span style={{ fontSize: 11, color: MUTED }}>
                      {t('membership.growthThreshold')} ≥ {tier.threshold.toLocaleString()}
                    </span>
                  )}
                  {tab === 'seller' && tier.commission && (
                    <span style={{
                      marginLeft: 'auto', fontSize: 11, padding: '2px 8px',
                      borderRadius: 4, background: `${tier.color}15`, color: tier.color,
                    }}>{t('membership.commissionLabel')} {tier.commission}</span>
                  )}
                </div>
                {isCurrent && next && (
                  <div style={{ fontSize: 12, color: MUTED }}>
                    {t('membership.needToUpgrade')} <span style={{ color: accentColor, fontWeight: 600 }}>
                      {(next.threshold - currentGrowth).toLocaleString()}
                    </span> {t('membership.growthUnit')}
                  </div>
                )}
                {isReached && !isCurrent && (
                  <div style={{ fontSize: 12, color: '#999' }}>{t('membership.reached')}</div>
                )}
                {!isReached && (
                  <div style={{ fontSize: 12, color: '#CCC' }}>
                    {t('membership.needMore')} {(tier.threshold - currentGrowth).toLocaleString()} {t('membership.growthUnit')}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Rules */}
        <div style={{
          margin: '16px 16px 0', background: '#fff', borderRadius: 10,
          border: `1px solid ${BD}`, padding: '16px',
        }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: INK, marginBottom: 12 }}>{t('membership.rules')}</div>
          {[
            tab === 'buyer'
              ? t('membership.ruleBuyerGrowth')
              : t('membership.ruleSellerGrowth'),
            t('membership.ruleOrderGrowth'),
            t('membership.ruleGrowthExpiry'),
            t('membership.ruleLevelUpdate'),
            tab === 'seller'
              ? t('membership.ruleSellerCommission')
              : t('membership.ruleBuyerBenefits'),
          ].map((rule, i) => (
            <div key={i} style={{
              display: 'flex', gap: 8, marginBottom: 8,
              fontSize: 13, color: MUTED, lineHeight: 1.6,
            }}>
              <span style={{ color: accentColor, flexShrink: 0 }}>{i + 1}.</span>
              <span>{rule}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
